import { Dispatch } from 'redux';

import { decreaseLoading, increaseLoading, LoadingAction } from './loading';
import { TransactionType } from '@portal/models/enumerators';
import { PAYMENT_LINK_URL } from '@portal/config/env';
import { sendEvent } from '@portal/services/firebase';
import { EventType } from '@portal/enum/analytics';
import { ThunkAction } from 'redux-thunk';
import { payment } from '@portal/config/app.json';
import ReceivableApi, {
  CreateManualReceivable,
  PaymentLink,
} from '@portal/repositories/receivable';
import {
  RECEIVABLE_PREVIEW,
  RECEIVABLE_GROUP_REPORT,
  RECEIVABLE_REPORT,
  MANUAL_RECEIVABLE_ADDRESS,
  RECEIVABLE_LINK,
  MANUAL_RECEIVABLE_RESULT,
  NEW_RECEIVABLE_REPORT,
} from './actionTypes';

import Errors, { ErrorsMessages } from '@portal/enum/errors';
import OnboardingApi from '@portal/repositories/onboarding';
import * as MessageService from '@portal/services/message';

export type GetReceivableGroupAction = {
  type: string;
  payload: models.PaginationResponse<models.ReceivableGroup>;
};

export type GetReceivableAction = {
  type: string;
  payload: models.PaginationResponse<models.Receivable>;
};

export type GetReceivablePreviewAction = {
  type: string;
  payload: models.ReceivablePreview | null;
};

export type GetAddressAction = {
  type: string;
  payload: models.Address | null;
};

export type GetReceivableLink = {
  type: string;
  payload: string | null;
};

export type GetManualReceivableResult = {
  type: string;
  payload: models.ManualReceivableResult | null;
};

export type GetExportedResponseCnabAction = {
  type: string;
  payload: models.Receivable | null;
};

export const getReceivablesGroups =
  (searchParams?: advancedFilterModels.baseFilter) =>
  async (dispatch: Dispatch<GetReceivableGroupAction | LoadingAction>) => {
    dispatch(increaseLoading());
    try {
      const payload: models.PaginationResponse<models.ReceivableGroup> =
        await ReceivableApi.getReceivableGroups({
          ...(searchParams
            ? {
                ...searchParams,
                offset: searchParams.page,
                limit: searchParams.pageSize,
              }
            : {
                pageSize: 10,
                page: 1,
                orderBy: 'createdAt',
                sort: 'desc',
              }),
        });
      dispatch({
        payload,
        type: RECEIVABLE_GROUP_REPORT,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getReceivablesByGroupId =
  (id: string, searchParams: advancedFilterModels.PaymentsFileAdvancedFilter) =>
  async (dispatch: Dispatch<GetReceivableAction | LoadingAction>) => {
    dispatch(increaseLoading());
    try {
      const payload: models.PaginationResponse<models.Receivable> =
        await ReceivableApi.getReceivables(id, {
          ...searchParams,
          offset: searchParams.page,
          limit: searchParams.pageSize,
        });

      dispatch({
        payload,
        type: RECEIVABLE_REPORT,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const uploadAndPreviewReceivable =
  (file: File, fileExtension: string, fileName: string) =>
  async (dispatch: Dispatch<GetReceivablePreviewAction | LoadingAction>) => {
    dispatch(increaseLoading());
    try {
      const receivableBucket = await ReceivableApi.getUrl({
        fileExtension,
      });

      await ReceivableApi.uploadFile(file, fileExtension, receivableBucket.url);

      const completeParams = {
        bucket: receivableBucket.bucket,
        name: fileName,
      };

      const completePayload = await ReceivableApi.getPreview(completeParams);

      const payload = {
        ...completePayload,
        bucket: receivableBucket.bucket,
      };

      dispatch({
        payload,
        type: RECEIVABLE_PREVIEW,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const cleanPreview = () => ({
  payload: null,
  type: RECEIVABLE_PREVIEW,
});

export const createReceivable =
  (
    fileBucket: string,
    fileName: string,
    pin: string
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | GetReceivableGroupAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const completeParams = {
        bucket: fileBucket,
        name: fileName,
        authenticate: {
          pin,
        },
      };

      await ReceivableApi.createFile(completeParams);
      dispatch(getReceivablesGroups());
      MessageService.success(
        'PAGES.PAYMENT.GENERATE_BY_FILE.MESSAGE.SUCCESS_RECEIVE_BY_LOT'
      );
      sendEvent(EventType.RECEIVABLE_FILE_ADDED);
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getAddress =
  (
    cep: string
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | GetAddressAction
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const payload = await OnboardingApi.getAddress(cep);
      dispatch({ type: MANUAL_RECEIVABLE_ADDRESS, payload });
    } catch (err) {
      MessageService.error('ADDRESS_GENERIC_ERROR');
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const cleanAddress = () => ({
  type: MANUAL_RECEIVABLE_ADDRESS,
  payload: null,
});

export const createManualReceivable =
  (
    receivable: models.ManualReceivableFields
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | GetManualReceivableResult
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      if (
        !receivable.amount ||
        !receivable.taxIdentifier ||
        !receivable.type ||
        !receivable.name
      ) {
        return MessageService.error('ALL_FIELDS');
      }

      let params: CreateManualReceivable = {
        amount: receivable.amount / 100,
        name: receivable.name,
        taxIdentifier: receivable.taxIdentifier,
        type: receivable.type,
      };

      if (
        receivable.type === TransactionType.BILLET ||
        receivable.type === TransactionType.BILLET_PIX
      ) {
        if (
          receivable.type === TransactionType.BILLET &&
          payment.receivableAddressInBilletCharge
        ) {
          params = {
            ...params,
            pixInfo: {
              key: '',
              charge: {
                address: {
                  city: receivable.city ?? '',
                  country: 'BRA',
                  state: receivable.state ?? '',
                  street: receivable.street ?? '',
                  zipCode: receivable.zipCode ?? '',
                  neighborhood: receivable.neighborhood ?? '',
                },
              },
            },
          };
        }
        params = {
          ...params,
          billetInfo: {
            discount: receivable.discount ?? 0,
            instructions: receivable.instructions ?? '',
            dueDate: receivable.dueDate ?? '',
            interest: receivable.interest ?? 0,
            fine: receivable.fine ?? 0,
          },
        };
      }

      if (
        receivable.type === TransactionType.PIX ||
        receivable.type === TransactionType.BILLET_PIX
      ) {
        if (
          !receivable.pixKey ||
          !receivable.city ||
          !receivable.state ||
          !receivable.street ||
          !receivable.zipCode
        ) {
          return MessageService.error('ALL_FIELDS');
        }
        if (!receivable.dueDate && receivable.type === TransactionType.PIX) {
          return MessageService.error('ALL_FIELDS');
        }

        params = {
          ...params,
          pixInfo: {
            key: receivable.pixKey,
            charge: {
              dueDate: receivable.dueDate ?? undefined,
              address: {
                city: receivable.city,
                country: 'BRA',
                state: receivable.state,
                street: receivable.street,
                zipCode: receivable.zipCode,
                neighborhood: receivable.neighborhood ?? undefined,
              },
            },
          },
        };
      }

      const payload = await ReceivableApi.createManualReceivable(params);
      MessageService.success('COMPONENTS.MANUAL_MODAL.MESSAGE.SUCCESS');

      dispatch({
        type: MANUAL_RECEIVABLE_RESULT,
        payload,
      });
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const cleanManualReceivableResult = () => ({
  payload: null,
  type: MANUAL_RECEIVABLE_RESULT,
});

export const getReceivableLink =
  (
    receivable: models.ManualReceivableFields
  ): ThunkAction<
    void,
    reducers.rootReducer,
    unknown,
    LoadingAction | GetReceivableLink
  > =>
  async (dispatch) => {
    dispatch(increaseLoading());
    try {
      if (
        !receivable.amount ||
        !receivable.taxIdentifier ||
        !receivable.name ||
        !receivable.pixKey ||
        !receivable.dueDate ||
        !receivable.neighborhood
      ) {
        return MessageService.error('ALL_FIELDS');
      }

      const paymentLink: PaymentLink = {
        amount: receivable.amount / 100,
        description: receivable.instructions ?? '',
        dueDate: receivable.dueDate,
        payerName: receivable.name,
        payerTaxIdentifier: receivable.taxIdentifier,
        pixKey: receivable.pixKey,
        payerAddress: {
          zipCode: receivable.zipCode ?? null,
          city: receivable.city ?? null,
          state: receivable.state ?? null,
          street: receivable.street ?? null,
          addressNumber: null,
          complement: null,
          neighborhood: receivable.neighborhood,
        },
      };

      const payload = await ReceivableApi.getReceivableLink(paymentLink);

      if (!RECEIVABLE_LINK || !PAYMENT_LINK_URL || !payload.id) {
        throw new Error(
          !RECEIVABLE_LINK || !PAYMENT_LINK_URL
            ? Errors.MISSING_PAYMENT_LINK
            : Errors.MISSING_PAYLOAD_ID
        );
      }

      dispatch({
        type: RECEIVABLE_LINK,
        payload: `${PAYMENT_LINK_URL}/${payload.id}`,
      });
      MessageService.success(
        'COMPONENTS.MANUAL_MODAL.MESSAGE.PAYMENT_LINK_SUCCESS'
      );
      sendEvent(EventType.RECEIVABLE_GENERATE_PAYMENT_LINK);
    } catch (err) {
      if (err instanceof Error) {
        if (err.message === Errors.MISSING_PAYMENT_LINK) {
          return MessageService.error(err.message, { originalError: err });
        }

        if (err.message === Errors.MISSING_PAYLOAD_ID) {
          return MessageService.error(err.message, { originalError: err });
        }

        if (err instanceof Error) {
          MessageService.error(err.message, { originalError: err });
        }
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const cleanReceivableLink = () => ({
  type: RECEIVABLE_LINK,
  payload: null,
});

export const getExportedResponseCnab =
  (id: string) =>
  async (dispatch: Dispatch<GetExportedResponseCnabAction | LoadingAction>) => {
    dispatch(increaseLoading());
    try {
      const responseCnabUrl = await ReceivableApi.getExportedResponseCnab(
        id,
        false
      );

      if (responseCnabUrl.url) {
        window.location.assign(responseCnabUrl.url);
      } else {
        throw new Error(ErrorsMessages.UNAVAILABLE_RETURN_FILE);
      }
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(ErrorsMessages.UNAVAILABLE_RETURN_FILE);
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const getNewReceivable =
  (params: advancedFilterModels.NewReceivableParams) => async (dispatch) => {
    dispatch(increaseLoading());
    try {
      const payload = await ReceivableApi.getNewReceivable(params);
      dispatch({ type: NEW_RECEIVABLE_REPORT, payload });
    } catch (err) {
      MessageService.error('NEW_RECEIVABLE_ERROR');
    } finally {
      dispatch(decreaseLoading());
    }
  };

export const postSendReceivableByEmail =
  (billetUrl: string, email: string) =>
  async (dispatch: Dispatch<LoadingAction>) => {
    dispatch(increaseLoading());
    try {
      await ReceivableApi.postSendReceivableByEmail(billetUrl, email);
    } catch (err) {
      if (err instanceof Error) {
        MessageService.error(err.message, { originalError: err });
      }
    } finally {
      dispatch(decreaseLoading());
    }
  };
